import { notification } from 'antd'

import ExpandableNotificationContent, {
  XERO_NOTIFICATION_PREVIEW_LENGTH,
} from './components/ExpandableNotificationContent'
import {
  NotificationType,
  NotificationTypeDisplayName,
  PMS_UPLOAD_FAILED_KEY,
  RealtimeNotificationData,
  XERO_UPLOAD_FAILED_KEY,
} from './types'

type ToastArgs = Parameters<typeof notification.open>[0]

export type ToastState = {
  /** The full message is shown. */
  expanded: boolean
  /** The user has interacted with the toast, so it no longer closes by itself. */
  pinned: boolean
}

const INFO_DURATION_SECONDS = 5
/** A PMS upload failure stays a little longer, so staff can read which files are missing. */
const PMS_ERROR_DURATION_SECONDS = 10
const EXPANDED_WIDTH = 480

export const getNotificationTypeDisplayName = (type: NotificationType) => {
  return NotificationTypeDisplayName[type.toString()] || type.toString()
}

const getToastMessage = (
  data: RealtimeNotificationData,
  isXeroError: boolean,
  isPmsError: boolean
) => {
  if (isXeroError) {
    return 'Xero upload failed'
  }
  if (isPmsError) {
    return 'PMS upload failed'
  }
  return getNotificationTypeDisplayName(data.type)
}

/**
 * Builds the antd toast for a realtime notification.
 * - Xero upload failures are red and stay open.
 * - PMS upload failures are red and close after 10s, unless the user opened "View Details".
 * "View Details" expands the same toast (re-opened with the same key) instead of navigating away.
 */
export const buildToast = (
  data: RealtimeNotificationData,
  key: string,
  state: ToastState,
  onToggle: () => void
): { kind: 'error' | 'info'; config: ToastArgs } => {
  const isXeroError = data.templateKey === XERO_UPLOAD_FAILED_KEY
  const isPmsError = data.templateKey === PMS_UPLOAD_FAILED_KEY
  const autoCloseSeconds = isPmsError
    ? PMS_ERROR_DURATION_SECONDS
    : INFO_DURATION_SECONDS

  return {
    kind: isXeroError || isPmsError ? 'error' : 'info',
    config: {
      key,
      message: getToastMessage(data, isXeroError, isPmsError),
      description: (
        <>
          <div>{data.title}</div>
          <ExpandableNotificationContent
            content={data.content}
            previewLength={
              isXeroError ? XERO_NOTIFICATION_PREVIEW_LENGTH : undefined
            }
            expanded={state.expanded}
            onToggle={onToggle}
          />
        </>
      ),
      duration: isXeroError || state.pinned ? 0 : autoCloseSeconds,
      style: state.expanded ? { width: EXPANDED_WIDTH } : undefined,
    },
  }
}
